import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import * as R from 'ramda';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { DataQueryKey } from '../../../../hooks/useDataQuery/dataQuery.types';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import useHandleApiError from '../../../../hooks/useHandleApiError';
import LangEnum from '../../../../lang/LangEnum';
import { getFindProductGridKey } from '../../../../services/modules/product-grid/product-grid.keys';
import { useProductDetailsCount } from '../../../../services/modules/productDetails/productDetails.queries';
import productDetailsRepository from '../../../../services/modules/productDetails/productDetails.repository';
import { mountProductDetailsFilterParams } from '../../../../services/modules/productDetails/productDetailsService.utils';
import useOrderBookStore from '../../../../stores/OrderBook/orderBook.store';
import useProductDetailsStore from '../../../../stores/ProductDetails/productDetails.store';
import {
  MassActionCombinationItem,
  ProductDetailsOriginType,
} from '../../../../stores/ProductDetails/productDetails.types';
import useIsAdminSeller from '../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import snackbarStore from '../../../../stores/Snackbar.store';
import { buildOrderProductUniqueIdentifier } from '../../../../utils/getOrderProductUniqueIdentifier';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../context/loadingStatus/LoadingStatus.store';
import {
  IErrorData,
  LoadingStatus,
} from '../../../context/loadingStatus/loadingStatus.store.types';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import { OrderProductGridItem } from '../../../orders/products/orderProducts.types';
import { useTranslation } from '../../../translation/TranslationContext';
import {
  DetailsModalEntity,
  ProductDetailsDTO,
  ProductDetailsEntityType,
} from '../types/productDetails.types';
import { getAllProductColorIds } from '../utils/productDetailsContent.utils';
import useProductDetailsRefetch from './useProductDetailsRefetch';

interface UseProductDetailsMassActionsParams extends UsedInOfflineMode {
  entity?: DetailsModalEntity;
  selectedProductColorId?: string;
  onSuccessPasteGrid?: () => void;
}

const useProductDetailsMassActions = ({
  entity,
  selectedProductColorId,
  onSuccessPasteGrid,
  isOfflineMode = false,
}: UseProductDetailsMassActionsParams) => {
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const queryClient = useQueryClient();
  const isAdminSeller = useIsAdminSeller();
  const { team } = useApplicationContext();

  const { getApiErrorMessage } = useHandleApiError();
  const { handleLoadingStatus, loadingStatus } = useLoadingStatusStore(
    useShallow((state) => ({
      handleLoadingStatus: state.handleLoadingStatus,
      loadingStatus: state.loadingStatus,
    })),
  );
  const { refetchUsingOrderIds } = useProductDetailsRefetch(
    entity?.type === ProductDetailsEntityType.PRODUCT ? entity.data.productId : '',
  );
  const { maxItemsToPasteInProductDetails } = useFeatureFlag(isOfflineMode);
  const { reloadOrderItemsQuantity, selectedOrder } = useOrderBookStore();

  const {
    from,
    copiedGrid,
    productGridParams,
    pasteGridMassAction,
    relatedProductsFilterState,
    setPasteGridMassAction,
    resetPasteGridMassAction,
    removeCopiedGrid,
  } = useProductDetailsStore(
    useShallow((state) => ({
      from: state.from,
      copiedGrid: state.copiedGrid,
      productGridParams: state.productGridParams,
      pasteGridMassAction: state.pasteGridMassAction,
      relatedProductsFilterState: state.relatedProductsFilterState,
      removeCopiedGrid: state.removeCopiedGrid,
      setPasteGridMassAction: state.setPasteGridMassAction,
      resetPasteGridMassAction: state.resetPasteGridMassAction,
    })),
  );
  const catalogId = from?.type === ProductDetailsOriginType.CATALOG ? from.id : undefined;

  const toggleSelectAllItemsMassAction = useCallback(() => {
    if (pasteGridMassAction.isAllItemsSelected) {
      removeCopiedGrid();
      return;
    }

    setPasteGridMassAction({
      isAllItemsSelected: true,
      items: {},
      unselectedItems: {},
    });
  }, [pasteGridMassAction, removeCopiedGrid, setPasteGridMassAction]);

  const { data: countData } = useProductDetailsCount(
    {
      ...productGridParams,
      catalogId,
      isAdminSeller,
      productId: entity?.type === ProductDetailsEntityType.PRODUCT ? entity.data.productId : '',
      filterByRelatedProducts: relatedProductsFilterState,
      allProductColorIds: getAllProductColorIds(entity),
    },
    { enabled: !!entity && entity.type === ProductDetailsEntityType.PRODUCT },
  );

  const formatCopiedGrid = (copiedGrid: OrderProductGridItem[]) => {
    return copiedGrid.map((item: OrderProductGridItem) => ({
      sizeId: item.productSizeId,
      quantity: item.orderItemQuantity ?? 0,
    }));
  };

  const handlePasteGridError = (error: IErrorData<any>) => {
    const message = getApiErrorMessage(error, translate(LangEnum.GLOBAL_ERROR_MESSAGE));
    handleMessage({ type: 'error', message });
  };

  const invalidateAllProductGridQueries = async () => {
    if (entity?.type !== ProductDetailsEntityType.PRODUCT) {
      return;
    }

    const unselectedCombinations = new Map<string, MassActionCombinationItem>();
    Object.values(pasteGridMassAction.unselectedItems).forEach((item) => {
      const key = buildOrderProductUniqueIdentifier({
        availability: item.availability,
        productColorId: item.productColorId,
        deliveryWindowId: item.deliveryWindowId || null,
      });
      unselectedCombinations.set(key, item);
    });

    const cachedCombinations = queryClient.getQueriesData<
      InfiniteData<ApiResponse<ProductDetailsDTO[]>>
    >({
      predicate: (query) => {
        const { queryKey } = query;
        if (!queryKey.includes(DataQueryKey.PRODUCT_DETAILS_COMBINATIONS)) {
          return false;
        }

        return queryKey.some(
          (part) =>
            typeof part === 'object' &&
            part !== null &&
            'productId' in part &&
            part.productId === entity?.data.productId,
        );
      },
    });

    const allCombinations = cachedCombinations.flatMap(
      ([_queryKey, queryData]) =>
        queryData?.pages?.flatMap((page: ApiResponse<ProductDetailsDTO[]>) => page.data ?? []) ??
        [],
    );

    productGridParams.orderIds?.forEach((orderId) => {
      allCombinations.forEach((combination) => {
        const productGridQueryKey = getFindProductGridKey({
          orderId,
          isOfflineMode,
          productColorId: combination.productColorId,
          availability: combination.availability,
          deliveryWindowId: combination.deliveryWindowId || combination.futureStockDeliveryWindowId,
        });
        const key = buildOrderProductUniqueIdentifier({
          availability: combination.availability,
          productColorId: combination.productColorId,
          deliveryWindowId:
            combination.deliveryWindowId || combination.futureStockDeliveryWindowId || null,
        });

        if (unselectedCombinations.has(key)) {
          return;
        }
        queryClient.removeQueries({ queryKey: [team?.id, ...productGridQueryKey] });
      });
    });
  };

  const invalidateSelectedCombinations = () => {
    Object.values(pasteGridMassAction.items).forEach((combination) => {
      const productGridQueryKey = getFindProductGridKey({
        isOfflineMode,
        orderId: combination.orderId,
        productColorId: combination.productColorId,
        availability: combination.availability,
        deliveryWindowId: combination.deliveryWindowId,
      });

      queryClient.removeQueries({ queryKey: [team?.id, ...productGridQueryKey] });
    });
  };

  const batchPasteGrid = async () => {
    if (!copiedGrid.product || entity?.type !== ProductDetailsEntityType.PRODUCT) {
      return;
    }

    const formattedCopiedGrid = formatCopiedGrid(copiedGrid.grid);

    const params = mountProductDetailsFilterParams({
      productId: entity.data.productId,
      orderIds: productGridParams.orderIds,
      productColorId: productGridParams?.productColorId,
      selectedProductColorId,
      filterByRelatedProducts: relatedProductsFilterState,
      availability: productGridParams.availability ?? undefined,
      deliveryWindowIds: productGridParams.deliveryWindowIds,
      allProductColorIds: entity.data.allColors.map((color) => color.productColorId),
      catalogId,
    });

    await handleLoadingStatus({
      requestFn: () =>
        productDetailsRepository().postBatchPasteGrid({
          isAdminSeller,
          copiedGrid: formattedCopiedGrid,
          pasteInAllCombinations: pasteGridMassAction.isAllItemsSelected,
          combinations: Object.values(pasteGridMassAction.items),
          unselectedCombinations: Object.values(pasteGridMassAction.unselectedItems),
          ...params,
        }),
      onFailure: (error) => {
        handlePasteGridError(error);
      },
      onSuccess: async () => {
        refetchUsingOrderIds(productGridParams.orderIds || []);
        removeCopiedGrid();
        resetPasteGridMassAction();

        if (
          pasteGridMassAction.isAllItemsSelected ||
          !R.isEmpty(pasteGridMassAction.unselectedItems)
        ) {
          invalidateAllProductGridQueries();
          await Promise.all(
            productGridParams.orderIds?.map((orderId) =>
              reloadOrderItemsQuantity(true, orderId, selectedOrder?.id === orderId),
            ) ?? [],
          );
        } else {
          invalidateSelectedCombinations();
          await Promise.all(
            Object.values(pasteGridMassAction.items).map((item) =>
              reloadOrderItemsQuantity(true, item.orderId, selectedOrder?.id === item.orderId),
            ),
          );
        }
      },
    });

    onSuccessPasteGrid?.();
  };

  const totalItems = !countData ? 0 : R.sum(countData.groups) - 1;
  const getTotalSelectedItems = useCallback(() => {
    if (pasteGridMassAction.isAllItemsSelected) {
      return totalItems;
    }

    if (!R.isEmpty(pasteGridMassAction.unselectedItems)) {
      return totalItems - Object.keys(pasteGridMassAction.unselectedItems).length;
    }

    return Object.keys(pasteGridMassAction.items).length;
  }, [
    totalItems,
    pasteGridMassAction.items,
    pasteGridMassAction.unselectedItems,
    pasteGridMassAction.isAllItemsSelected,
  ]);

  const totalSelected = getTotalSelectedItems();

  const itemsAbovePermittedAmount = totalSelected > maxItemsToPasteInProductDetails;
  const minItemsIsNotSelected = totalSelected === 0;
  const canPasteGrid = !itemsAbovePermittedAmount && !minItemsIsNotSelected;

  const getBlockReasonMessage = (): string | undefined => {
    if (itemsAbovePermittedAmount) {
      return translate(
        LangEnum.BLOCK_PASTE_GRID_WITH_ITEMS_ABOVE_PERMITTED_AMOUNT_TOOLTIP_MESSAGE,
        { maxItems: maxItemsToPasteInProductDetails },
      );
    }

    if (minItemsIsNotSelected) {
      return translate(LangEnum.BLOCK_PASTE_GRID_WITH_NO_ITEMS_TOOLTIP_MESSAGE);
    }
  };

  return {
    totalItems,
    totalSelected,
    canPasteGrid,
    batchPasteGrid,
    toggleSelectAllItemsMassAction,
    deselectAllItemsMassAction: removeCopiedGrid,
    massActionBlockReasonMessage: getBlockReasonMessage(),
    isLoadingPasteGridMassAction: loadingStatus === LoadingStatus.LOADING,
  };
};

export default useProductDetailsMassActions;
